import * as THREE from "three";
import type { HqDim } from "../../hqAnchors";
import { FIT_MODES, type FitMode, type FitSpec } from "./bigTypes";
import { CAMERA_FACING, type SocketFrame } from "./sockets";

/**
 * Fitted pieces (big kits): one replacement roof / cladding / plinth piece that fits every tower × tier. The piece is
 * authored in a UNIT envelope (1 m across about its origin, 1 m tall, origin at the bottom centre); the envelope it is
 * fitted to is measured per model by tools/art/skins/sockets.mjs into anchors.json `dims` (`fromBox` stands in for a
 * model that has none). Applied once, when the kit geometry is built (kit/kitGeometry.ts): no per-frame cost.
 */

/** The measured envelopes of one model, by fit mode (a model without a roof has no `roof`). */
export type FitDims = Partial<Record<FitMode, HqDim>>;

/** A use's `fitTo` as a full spec. */
export function fitSpecOf(f: FitMode | FitSpec): FitSpec { return typeof f === "string" ? { to: f } : f; }

/** The unit envelope's radial (x, z) and height (y) scales for a spec on an envelope. */
export function fitScale(dim: HqDim, spec: FitSpec): { x: number; y: number; z: number } {
  const grow = 1 + (spec.margin ?? 0);
  const box = spec.shape === "box";
  const x = 2 * (box ? dim.rx : dim.r) * grow, z = 2 * (box ? dim.rz : dim.r) * grow;
  const across = (x + z) / 2;
  const y = spec.height === "keep" ? across : spec.height === "min" ? Math.min(dim.h, across) : dim.h;
  return { x, y, z };
}

/**
 * The frame a fitted piece stands in: the envelope's bottom centre, up +Y; round pieces face the game camera like the
 * apex (CAMERA_FACING), box pieces keep the model's axes (their x and z are the part's own half-extents) with their
 * front on the model's +z, the camera's side of the box.
 */
export function fitFrame(dim: HqDim, spec: FitSpec): SocketFrame {
  const up: [number, number, number] = [0, 1, 0];
  const out: [number, number, number] = spec.shape === "box" ? [0, 0, 1] : [CAMERA_FACING[0], CAMERA_FACING[1], CAMERA_FACING[2]];
  const right: [number, number, number] = [up[1] * out[2] - up[2] * out[1], up[2] * out[0] - up[0] * out[2], up[0] * out[1] - up[1] * out[0]];
  return { name: "apex", part: dim.part, p: [dim.c[0], dim.c[1], dim.c[2]], up, out, right };
}

/**
 * The model-space matrix of a fitted piece: the frame, then the use's offset (metres) and yaw, then the envelope's
 * scale times the use's own (`k`, the tiered `scale`).
 */
export function fitMatrix(dim: HqDim, spec: FitSpec, k: number, offset: readonly number[] = [0, 0, 0], yawDeg = 0): THREE.Matrix4 {
  const f = fitFrame(dim, spec), s = fitScale(dim, spec);
  const basis = new THREE.Matrix4().makeBasis(new THREE.Vector3(...f.right), new THREE.Vector3(...f.up), new THREE.Vector3(...f.out));
  basis.setPosition(...f.p);
  return basis.multiply(new THREE.Matrix4().makeTranslation(offset[0], offset[1], offset[2]))
    .multiply(new THREE.Matrix4().makeRotationY(THREE.MathUtils.degToRad(yawDeg)))
    .multiply(new THREE.Matrix4().makeScale(s.x * k, s.y * k, s.z * k));
}

/**
 * A tapered fit's radial factor at unit height `y` (0 the bottom, 1 the top), relative to the envelope's widest radius:
 * the part's bottom radius `r0` running to its top radius `r1`. 1 without `taper` (or for `box`).
 */
export function taperAt(dim: HqDim, spec: FitSpec, y: number): number {
  if (!spec.taper || spec.shape === "box" || dim.r <= 0) return 1;
  const t = Math.min(1, Math.max(0, y));
  return (dim.r0 + (dim.r1 - dim.r0) * t) / dim.r;
}

/** Fraction of a box's height each envelope spans when a model has no measured dims: [from, to]. */
const BOX_SPANS: Readonly<Record<FitMode, readonly [number, number, number]>> = {
  // [bottom fraction, top fraction, radius fraction of the box's half-width]
  tower: [0, 1, 1], drum: [0.03, 0.55, 0.8], gallery: [0.5, 0.62, 0.95], roof: [0.6, 1, 0.85], plinth: [0, 0.04, 1],
};
const PART_OF: Readonly<Record<FitMode, string>> = { tower: "drum", drum: "drum", gallery: "deck", roof: "roof", plinth: "plinth" };

/** Envelopes from a model's box (the stand-in when anchors.json has no `dims` for it: a rebake before the tool ran). */
export function fromBox(box: { min: readonly number[]; max: readonly number[] }): FitDims {
  const cx = (box.min[0] + box.max[0]) / 2, cz = (box.min[2] + box.max[2]) / 2, H = box.max[1] - box.min[1];
  const hx = (box.max[0] - box.min[0]) / 2, hz = (box.max[2] - box.min[2]) / 2;
  const out: FitDims = {};
  for (const mode of FIT_MODES) {
    const [a, b, k] = BOX_SPANS[mode];
    const r = Math.max(hx, hz) * k;
    out[mode] = { part: PART_OF[mode], c: [cx, box.min[1] + H * a, cz], h: H * (b - a), r, r0: r, r1: r, rx: hx * k, rz: hz * k };
  }
  return out;
}

/** A model's envelopes: the measured ones, any missing (but `roof`, which a roofless model lacks) from the box. */
export function dimsFor(measured: Readonly<Record<string, HqDim>> | undefined, box: { min: readonly number[]; max: readonly number[] }): FitDims {
  if (!measured) return fromBox(box);
  const fallback = fromBox(box), out: FitDims = {};
  for (const mode of FIT_MODES) {
    const d = measured[mode] ?? (mode === "roof" ? undefined : fallback[mode]);
    if (d) out[mode] = d;
  }
  return out;
}
